import { Typography } from '@material-tailwind/react';
import { useState } from 'react';
import {
  FreeTrialContextItem,
  FreeTrialStep,
  TRIAL_STEPS,
} from '../../constants/types/free-trial/type';
import FreeTrialLessonStep from './FreeTrialLessonStep';
import FreeTrialReviewStep from './FreeTrialReviewStep';
import FreeTrialRecitationStep from './FreeTrialRecitationStep';
import FreeTrialResultStep from './FreeTrialResultStep';
import { CheckIcon } from '@heroicons/react/24/outline';

interface FreeTrialExperienceSectionProps {
  freeTrialContext: FreeTrialContextItem;
}

const FreeTrialExperienceSection = ({
  freeTrialContext,
}: FreeTrialExperienceSectionProps) => {
  const [progressStep, setProgressStep] = useState<FreeTrialStep>(
    Math.min(
      Math.max(Object.keys(freeTrialContext.stepProgress ?? {}).length, 1),
      4,
    ) as FreeTrialStep,
  );
  const [selectedStep, setSelectedStep] = useState<FreeTrialStep>(1);

  const handleCompleteStep = (nextStep: FreeTrialStep) => {
    setProgressStep((prev) => Math.max(prev, nextStep) as FreeTrialStep);
    setSelectedStep(nextStep);
  };

  const renderStepContent = () => {
    switch (selectedStep) {
      case 1:
        return (
          <FreeTrialLessonStep
            freeTrialContext={freeTrialContext}
            onComplete={() => handleCompleteStep(2)}
          />
        );

      case 2:
        return (
          <FreeTrialReviewStep
            freeTrialContext={freeTrialContext}
            onComplete={() => handleCompleteStep(3)}
          />
        );

      case 3:
        return (
          <FreeTrialRecitationStep
            freeTrialContext={freeTrialContext}
            onComplete={() => handleCompleteStep(4)}
          />
        );

      case 4:
        return <FreeTrialResultStep />;

      default:
        return null;
    }
  };

  return (
    <>
      {/* 상위 학습 단계 */}
      <section className="mt-2 rounded-2xl bg-white p-2 shadow-[0_6px_20px_rgba(40,25,100,0.06)] md:mt-6 md:p-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {TRIAL_STEPS.map((item, index) => {
            const step = (index + 1) as FreeTrialStep;

            const isSelected = step === selectedStep;
            const isCurrent = step === progressStep;
            const isCompleted = step < progressStep;
            const isAccessible = step <= progressStep;

            const isViewingPreviousStep = selectedStep < progressStep;

            // 결과 화면에 실제로 진입해 있는 경우
            const isResultCompleted = progressStep === 4 && selectedStep === 4;

            // 이전 단계를 보고 있을 때는
            // 실제 진행 단계는 체크가 아니라 숫자/별로 보여준다.
            const shouldShowCheck =
              isResultCompleted ||
              (isCompleted && !(isViewingPreviousStep && isCurrent));

            return (
              <button
                key={item.title}
                type="button"
                disabled={!isAccessible}
                onClick={() => setSelectedStep(step)}
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-left transition ${
                  isSelected ? 'bg-[#F3EFFF]' : ''
                } ${
                  isAccessible
                    ? 'cursor-pointer hover:bg-[#F7F4FF]'
                    : 'cursor-default'
                }`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-bold transition md:h-8 md:w-8 md:text-[14px] ${
                    shouldShowCheck
                      ? 'border-[#6737F4] bg-[#6737F4] text-white'
                      : isSelected
                        ? 'border-[#6737F4] bg-white text-[#6737F4] ring-2 ring-[#6737F4]/20 ring-offset-1'
                        : isCurrent
                          ? 'border-[#6737F4] bg-white text-[#6737F4]'
                          : 'border-gray-200 bg-gray-100 text-gray-500'
                  }`}
                >
                  {shouldShowCheck ? (
                    <CheckIcon className="h-4 w-4 stroke-[3]" />
                  ) : (
                    item.step
                  )}
                </div>

                <div className="min-w-0">
                  <Typography
                    className={`whitespace-nowrap text-[13px] font-extrabold md:text-[14px] ${
                      isSelected || isCurrent
                        ? 'text-gray-800'
                        : isCompleted
                          ? 'text-gray-700'
                          : 'text-gray-500'
                    }`}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    className={`whitespace-nowrap text-[11px] font-bold md:text-[12px] ${
                      isSelected
                        ? 'text-[#6737F4]'
                        : isCurrent
                          ? 'text-[#6737F4]'
                          : isCompleted
                            ? 'text-gray-500'
                            : 'text-gray-400'
                    }`}
                  >
                    {isSelected
                      ? progressStep === 1
                        ? '시작 전 안내'
                        : '지금 여기'
                      : isCurrent && isViewingPreviousStep
                        ? '열렸어요'
                        : isCompleted
                          ? '완료 · 다시 보기'
                          : '잠겨 있어요'}
                  </Typography>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {renderStepContent()}
    </>
  );
};

export default FreeTrialExperienceSection;
